/**
 * @file InputNumber 数字输入
 * @description 紧凑数字框；步进按钮；受控 value。
 *
 * @whenToUse 数量、金额、库存、排序权重等数值字段。
 * @whenNotToUse 普通文本（用 Input）；滑杆调节（用 Slider）。
 */

import type { FocusEventHandler } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type InputNumberProps = {
  value?: number
  onChange?: (value: number | undefined) => void
  /** 输入框获得焦点 */
  onFocus?: FocusEventHandler<HTMLInputElement>
  /** 输入框失去焦点 */
  onBlur?: FocusEventHandler<HTMLInputElement>
  min?: number
  max?: number
  step?: number
  /** 小数精度；不传则不强制 */
  precision?: number
  disabled?: boolean
  invalid?: boolean
  placeholder?: string
  className?: string
  id?: string
}

function clamp(value: number, min?: number, max?: number): number {
  let next = value
  if (min !== undefined) next = Math.max(min, next)
  if (max !== undefined) next = Math.min(max, next)
  return next
}

function roundPrecision(value: number, precision?: number): number {
  if (precision === undefined) return value
  const factor = 10 ** precision
  return Math.round(value * factor) / factor
}

export function InputNumber({
  value,
  onChange,
  onFocus,
  onBlur,
  min,
  max,
  step = 1,
  precision,
  disabled = false,
  invalid = false,
  placeholder,
  className,
  id,
}: InputNumberProps) {
  const commit = (next: number | undefined) => {
    if (next === undefined) {
      onChange?.(undefined)
      return
    }
    onChange?.(roundPrecision(clamp(next, min, max), precision))
  }

  const atMin = value !== undefined && min !== undefined && value <= min
  const atMax = value !== undefined && max !== undefined && value >= max

  return (
    <div
      className={cn(
        'flex h-9 w-full items-center rounded-lg border-2 bg-raised transition-opacity duration-200',
        invalid ? 'border-danger' : 'border-border',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <button
        type="button"
        aria-label="减少"
        disabled={disabled || atMin}
        className="inline-flex h-full w-9 shrink-0 items-center justify-center border-r border-border text-muted transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() =>
          commit(roundPrecision((value ?? 0) - step, precision))
        }
      >
        <Minus className="size-4" aria-hidden />
      </button>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        disabled={disabled}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        className="h-full min-w-0 flex-1 border-0 bg-transparent px-2 text-center text-sm tabular-nums text-foreground"
        value={value === undefined ? '' : String(value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(event) => {
          const raw = event.target.value.trim()
          if (raw === '' || raw === '-') {
            commit(undefined)
            return
          }
          const parsed = Number(raw)
          if (Number.isNaN(parsed)) return
          commit(parsed)
        }}
      />
      <button
        type="button"
        aria-label="增加"
        disabled={disabled || atMax}
        className="inline-flex h-full w-9 shrink-0 items-center justify-center border-l border-border text-muted transition-opacity duration-200 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        onClick={() =>
          commit(roundPrecision((value ?? 0) + step, precision))
        }
      >
        <Plus className="size-4" aria-hidden />
      </button>
    </div>
  )
}
